import { ReactNode, useRef, useState, useEffect } from "react";
import { useQueryState } from "nuqs";

import CaseStudyModal from "./CaseStudyModal";
import { UseCaseSlideProps } from "../types";
import ModalCardContent from "./ModalCardContent";

import AmazonConnectLaunchPlustGenAIChatBot from "./caseStudies/AmazonConnectLaunchPlustGenAIChatBot";
import AmazonConnectWithAI from "./caseStudies/AmazonConnectWithAI";
import AmazonConnectWithAIAutomationAnalytics from "./caseStudies/AmazonConnectWithAIAutomationAnalytics";
import AmazonConnectManagedService from "./caseStudies/AmazonConnectManagedServiceIntelligentAI";
import DigitalConcierge from "./caseStudies/DigitalConcierge";
import SalesforceAmazonConnectIntegrationAndIntelligentBotDeployment from "./caseStudies/SalesforceAmazonConnectIntegrationAndIntelligentBotDeployment";
import AcceleratingCollegeAdmissionsWithAI from "./caseStudies/AcceleratingCollegeAdmissionsWithAI";
import PioneeringOmnichannelCloudContactCenterWithAmazonConnectForShippingAndLogistics from "./caseStudies/PioneeringOmnichannelCloudContactCenterWithAmazonConnectForShippingAndLogistics";

const CardSliderCaseStudies = ({ cards }: UseCaseSlideProps) => {
  const [article, setArticle] = useQueryState("case");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [thumbWidth, setThumbWidth] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Calculate thumb width and track scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateScrollInfo = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScroll = scrollWidth - clientWidth;

      // Calculate thumb width as percentage of visible area
      const visibleRatio = clientWidth / scrollWidth;
      setThumbWidth(Math.max(visibleRatio * 100, 20)); // Min 20% width

      // Calculate scroll progress
      if (maxScroll > 0) {
        setScrollProgress((scrollLeft / maxScroll) * 100);
      }
    };

    updateScrollInfo();
    container.addEventListener("scroll", updateScrollInfo);
    window.addEventListener("resize", updateScrollInfo);

    return () => {
      container.removeEventListener("scroll", updateScrollInfo);
      window.removeEventListener("resize", updateScrollInfo);
    };
  }, [cards]);

  // Mouse drag handlers for the cards container
  const handleMouseDown = (e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setIsDragging(true);
    setStartX(e.pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const container = scrollContainerRef.current;
    if (!container) return;
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5; // Scroll speed multiplier
    container.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Handle scrollbar click/drag
  const handleScrollbarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current;
    const scrollbar = e.currentTarget;
    if (!container || !scrollbar) return;

    const rect = scrollbar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const scrollbarWidth = rect.width;
    const { scrollWidth, clientWidth } = container;
    const maxScroll = scrollWidth - clientWidth;

    // Calculate where to scroll based on click position
    const clickRatio = clickX / scrollbarWidth;
    container.scrollLeft = clickRatio * maxScroll;
  };

  const handleOpenModal = (index: number) => {
    setArticle(cards[index].title);
  };

  const handleCloseModal = () => {
    setArticle(null);
  };

  const selectedCard = cards.find((card) => card.title === article);
  const isModalOpen = Boolean(article);

  const cardComponents: Record<string, ReactNode> = {
    "Amazon Connect Launch + GenAI Chatbot": (
      <AmazonConnectLaunchPlustGenAIChatBot />
    ),
    "Amazon Connect with AI, Automation & Analytics": (
      <AmazonConnectWithAIAutomationAnalytics />
    ),
    "Amazon Connect Managed Service & Intelligent AI Analytics": (
      <AmazonConnectManagedService />
    ),
    "Amazon Connect Managed Service": <DigitalConcierge />,
    "Amazon Connect Launch + iCXeed Managed Service": <AmazonConnectWithAI />,
    "Salesforce-Amazon Connect Integration and Intelligent Bot Deployment": (
      <SalesforceAmazonConnectIntegrationAndIntelligentBotDeployment />
    ),
    "Accelerating College Admissions with AI": (
      <AcceleratingCollegeAdmissionsWithAI />
    ),
    "Pioneering Omnichannel Cloud Contact Center with Amazon Connect for Shipping and Logistics":
      (
        <PioneeringOmnichannelCloudContactCenterWithAmazonConnectForShippingAndLogistics />
      ),
  };

  // Calculate thumb position
  const thumbPosition = (scrollProgress / 100) * (100 - thumbWidth);

  return (
    <section className="card-slider-section">
      <div className="main-container-blk">
        {/* Horizontal scrollable container */}
        <div
          ref={scrollContainerRef}
          className={`flex gap-4 overflow-x-auto scrollbar-hidden ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {cards.map((card, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-full md:w-[calc((100%-16px)/2)] lg:w-[calc((100%-32px)/3)]"
            >
              <ModalCardContent
                card={card}
                index={index}
                onClick={handleOpenModal}
              />
            </div>
          ))}
        </div>

        {/* Custom Scrollbar */}
        {cards.length > 3 && (
          <div
            className="w-full h-3 bg-secondary-dark-navy-500 rounded-full cursor-pointer relative mt-6"
            onClick={handleScrollbarClick}
          >
            <div
              className="h-full bg-primary-blue rounded-full absolute top-0 transition-all duration-100"
              style={{
                width: `${thumbWidth}%`,
                left: `${thumbPosition}%`,
              }}
            />
          </div>
        )}
      </div>

      <CaseStudyModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        titleModal={selectedCard?.title || ""}
        type={selectedCard?.type || ""}
      >
        {selectedCard && cardComponents[selectedCard.title]}
      </CaseStudyModal>
    </section>
  );
};

export default CardSliderCaseStudies;
